import type {
  BuilderCampaignPayload,
  CampaignDraftDetail,
  FundingWallet,
} from "@/interfaces/campaignDraft";
import type { Product } from "@/interfaces/product";
import { apiClient } from "@/lib/apiClient";
import { downloadBlob } from "@/lib/csv";
import type { BudgetCollectionsResponse } from "@/types/types";

export async function fetchCollectionsByBudget(budget: number): Promise<BudgetCollectionsResponse> {
  const { data } = await apiClient.get<{ status: string } & BudgetCollectionsResponse>(
    "/campaign-builder/collections/by-budget",
    { params: { budget } },
  );
  return { collections: data.collections, domesticIndex: data.domesticIndex };
}

export async function fetchFundingWallets(): Promise<FundingWallet[]> {
  const { data } = await apiClient.get<{ success: boolean; wallets: FundingWallet[] }>(
    "/campaign-builder/funding-wallets",
  );
  return data.wallets;
}

// ─── Campaign Builder draft CRUD ──────────────────────────────────────────────
// Create returns the new id + the mock/demo token (isTest short URL) that powers Step 3's
// demo link + test email. Update accepts any partial slice of the builder payload.

export async function createCampaignDraft(
  payload: BuilderCampaignPayload,
): Promise<{ id: number; mockToken: string; universalToken: string | null }> {
  const { data } = await apiClient.post<{
    success: boolean;
    id: number;
    mockToken: string;
    universalToken: string | null;
  }>("/campaign-builder/campaigns", payload);
  return { id: data.id, mockToken: data.mockToken, universalToken: data.universalToken ?? null };
}

export async function fetchCampaignDraft(
  id: number | string,
  path = `/campaign-builder/campaigns/${id}`,
): Promise<CampaignDraftDetail> {
  const { data } = await apiClient.get<{ success: boolean; campaign: CampaignDraftDetail }>(path);
  return data.campaign;
}

/**
 * Saves a step, and reports the reminder slots the server silently cancelled because the
 * schedule or expiry moved out from under them (1-based). Empty on every ordinary save.
 */
export async function updateCampaignDraft(
  id: number | string,
  payload: BuilderCampaignPayload,
): Promise<{ cancelledReminderOrders: number[]; universalToken: string | null }> {
  const { data } = await apiClient.patch<{
    cancelledReminderOrders?: number[];
    universalToken?: string | null;
  }>(`/campaign-builder/campaigns/${id}`, payload);
  return {
    cancelledReminderOrders: data?.cancelledReminderOrders ?? [],
    universalToken: data?.universalToken ?? null,
  };
}

export async function deleteCampaignDraft(id: number | string): Promise<void> {
  await apiClient.delete(`/campaign-builder/campaigns/${id}`);
}

/**
 * Fetch the manual-send gift-links CSV and trigger a browser download as `filename`
 * (`Campaign Name - $Price.csv`, built by the caller with `campaignCsvFilename`). Goes through
 * apiClient (not a plain `<a href>`/window.open) so the Clerk JWT is attached — the endpoint is
 * scope-guarded — then hands the bytes to the shared `downloadBlob`, whose `download` attribute
 * names the file regardless of the response's own Content-Disposition.
 */
export async function downloadGiftLinksCsv(id: number | string, filename: string): Promise<void> {
  const { data } = await apiClient.get(`/campaign-builder/campaigns/${id}/gift-links.csv`, {
    responseType: "blob",
  });
  const blob = data instanceof Blob ? data : new Blob([data as BlobPart], { type: "text/csv" });
  downloadBlob(filename, blob);
}

export interface SendTestEmailResult {
  success: boolean;
  sent: number;
  failed: number;
  total: number;
  results: Array<{ email: string; success: boolean; error?: string }>;
}

/** Step 3's "Send a Test Email" — sends the draft's current design to up to 10 addresses. */
export async function sendCampaignTestEmail(
  id: number | string,
  emails: string[],
): Promise<SendTestEmailResult> {
  const { data } = await apiClient.post<SendTestEmailResult>(
    `/campaign-builder/campaigns/${id}/send-test`,
    { emails },
  );
  return data;
}

// Product is re-exported for callers that build gift payloads from the collection grid.
export type { Product };
